import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import styles from "./NovelsPage.module.css";
import SLEEPING_IRON_MD from "./content/sleepingIronMarkdown.js";

/**
 * The Shelf — fiction showcase.
 * Prose lives in `src/assets/noir/睡铁.md`; after edits run `npm run sync:shelf-md`.
 */
const WORKS = [
  {
    id: 1,
    title: "Sleeping Iron",
    titleDisplay: "SLEEPING IRON",
    genre: "Mecha sci-fi · political fiction",
    length: "A.J Liu · long-form serial · Markdown manuscript",
    status: "IN PROGRESS",
    logline:
      "A mecha-and-politics story drawn from the author's own life. Right now the spark mostly carries everyday slice-of-life; I'll take it slow and see whether it can grow into something with real bite. For the moment it's only a rough frame—not much on the page—something I poke at when the mood strikes, almost a side hobby. Past the outline I still don't know the concrete scenes or lines; I have a vague compass, but the whole thing feels a little flat. I'm hoping that if I keep digging while I write, sharper turns and character-driven arcs will surface. The pipe dream is to lift the final draft straight into a game script. Lets see how it goes.",
    markdown: SLEEPING_IRON_MD,
  },
];

const STATUS_COLOR = {
  "COMPLETE": "rgba(220,220,220,0.55)",
  "IN PROGRESS": "rgba(200,200,200,0.3)",
};

export default function NovelsPage() {
  const [active, setActive] = useState(WORKS[0]);

  const markdownWithTeaser = useMemo(() => {
    if (!active.markdown) return "";
    return `${active.markdown.trim()}\n\n---\n\n**To be continued.**\n`;
  }, [active.markdown]);

  return (
    <div className={styles.page}>
      <Link className="noir-back-portals" to="/noir-uni?skip=intro">← Noir Universe</Link>

      <header className={styles.masthead}>
        <p className={styles.kicker}>ACT III</p>
        <h1 className={styles.title}>The Shelf</h1>
        <p className={styles.subtitle}>
          Original long-form fiction in Chinese—the same draft pool I tap for game writing.
        </p>
        <div className={styles.rule} />
      </header>

      <main className={styles.main}>
        <nav className={styles.workList} aria-label="Works list">
          {WORKS.map((work) => (
            <button
              key={work.id}
              className={`${styles.workRow}${active.id === work.id ? ` ${styles.workRowActive}` : ""}`}
              onClick={() => setActive(work)}
              aria-pressed={active.id === work.id}
            >
              <div className={styles.workRowInner}>
                <span className={styles.workTitle}>{work.title}</span>
                <span className={styles.workGenre}>{work.genre}</span>
              </div>
              <span
                className={styles.workStatus}
                style={{ color: STATUS_COLOR[work.status] }}
              >
                {work.status}
              </span>
            </button>
          ))}
        </nav>

        <article className={styles.detail} key={active.id}>
          <div className={styles.detailHeader}>
            <p className={styles.detailGenre}>{active.genre}</p>
            <h2 className={styles.detailTitle}>{active.title}</h2>
            <p className={styles.detailTitleEn}>{active.titleDisplay}</p>
            <p className={styles.detailMeta}>{active.length}</p>
          </div>

          <div className={styles.detailDivider} />

          <p className={styles.logline}>{active.logline}</p>

          <div className={styles.excerptWrap}>
            <p className={styles.excerptLabel}>TEXT</p>
            <div className={styles.mdScroll}>
              <div className={styles.mdBody}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                  {markdownWithTeaser}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </article>
      </main>

      <p className={styles.footer}>THE END.</p>
    </div>
  );
}
